import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICanView, IUser } from '@models/IUser';
import { ISignInSettings } from '@srm/core/auth';
import { SLICE_NAMES } from '@/constants';

/** Причина `sessionExpired`, у которой свой текст тоста: в соседней вкладке вошёл другой пользователь. */
export type SessionExpiredCause = 'user-switch';

export interface IAuthState {
    isAuth: boolean;
    isLoading: boolean;
    isLoggingIn: boolean;
    isSmsOtp: boolean;
    error: string;
    user: IUser;
    /**
     * Есть access-токен сессии — зеркало `auth` (`startAuth`, `accessTokenChanged`), выход его снимает через то же
     * зеркало; токен соседней вкладки, профиль по которому не загрузился, сессией этой вкладки не считается. Сами
     * токены в стор не попадают: их видно в DevTools и снимках состояния, читают их через `auth`.
     */
    hasAccessToken: boolean;
    signInSettings: ISignInSettings | null;
    /**
     * Стенд недоступен (nginx 502/503/504 или сетевая ошибка) — обычно идёт обновление.
     * Ставится interceptor'ом `$api`, снимается поллингом `useApiAvailability`
     * на странице логина, когда `SignInSettings` снова отвечает.
     */
    apiUnavailable: boolean;
}

export interface IAuthError {
    message: string;
    login?: string;
    loginType?: 'LoginPass' | 'LoginPassTwoFactor' | 'SAML' | 'SSO';
}

/**
 * Данные прежнего пользователя больше не должны показываться: выход, истечение сессии или вход
 * другого пользователя. Страница при этом не перезагружается.
 */
export const isSessionEnd = (prev: IAuthState, next: IAuthState): boolean =>
    prev.isAuth && (!next.isAuth || (!!prev.user.id && next.user.id !== prev.user.id));

const initialState: IAuthState = {
    isAuth: false,
    isLoading: true,
    isLoggingIn: false,
    isSmsOtp: false,
    error: '',
    user: {
        canView: {} as ICanView,
    } as IUser,
    hasAccessToken: false,
    signInSettings: null,
    apiUnavailable: false,
};

export const AuthSlice = createSlice({
    name: SLICE_NAMES.AUTH,
    initialState,
    reducers: {
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsLoggingIn: (state, action: PayloadAction<boolean>) => {
            state.isLoggingIn = action.payload;
        },
        setIsSmsOtp: (state, action: PayloadAction<boolean>) => {
            state.isSmsOtp = action.payload;
        },
        setError: (state, action: PayloadAction<IAuthError>) => {
            state.error = action.payload.message;
        },
        successLogin: (state, action: PayloadAction<Pick<IAuthState, 'user'>>) => {
            state.isAuth = true;
            state.error = '';
            state.user.id = action.payload.user.id;
            state.user.hasImage = action.payload.user.hasImage;
            // photoVersion нужен для ?v=-версии аватара (immutable-кэш, переживает reload).
            // Без него после перезагрузки URL был бы ?v=0 и долгий кэш своего фото не включался.
            state.user.photoVersion = action.payload.user.photoVersion;
            state.user.employeeId = action.payload.user.employeeId;
            state.user.newEmployeeId = action.payload.user.newEmployeeId;
            state.user.employeeIksId = action.payload.user.employeeIksId;
            state.user.dummies = action.payload.user.dummies;
            state.user.assistantKeys = action.payload.user.assistantKeys;
            state.user.fullName = action.payload.user.fullName;
            state.user.name = action.payload.user.name;
            state.user.firstName = action.payload.user.firstName;
            state.user.lastName = action.payload.user.lastName;
            state.user.middleName = action.payload.user.middleName;
            state.user.position = action.payload.user.position;
            state.user.department = action.payload.user.department;
            state.user.departmentId = action.payload.user.departmentId;
            state.user.canUserDepartmentEdit = action.payload.user.canUserDepartmentEdit;
            state.user.canUserDepartmentView = action.payload.user.canUserDepartmentView;
            state.user.subdivision = action.payload.user.subdivision;
            state.user.subdivisionInn = action.payload.user.subdivisionInn;
            state.user.workGroups = action.payload.user.workGroups;
            state.user.accessGroups = action.payload.user.accessGroups;
            state.user.email = action.payload.user.email;
            state.user.username = action.payload.user.username;
            state.user.login = action.payload.user.login;
            state.user.role = action.payload.user.role;
            state.user.phone = action.payload.user.phone;
            state.user.customUserSettings = action.payload.user.customUserSettings;
            state.user.canView.settings = action.payload.user.canView.settings;
            state.user.canView.settingsProfile = action.payload.user.canView.settingsProfile;
            state.user.canView.settingsOrganization = action.payload.user.canView.settingsOrganization;
            state.user.canView.settingsSecurity = action.payload.user.canView.settingsSecurity;
            state.user.canView.settingsDevices = action.payload.user.canView.settingsDevices;
            state.user.canView.settingsDeleteAccount = action.payload.user.canView.settingsDeleteAccount;
            state.user.canView.settingsAvatar = action.payload.user.canView.settingsAvatar;
            state.user.canView.settingsNotifications = action.payload.user.canView.settingsNotifications;
            state.user.canView.usersActivation = action.payload.user.canView.usersActivation;
            state.user.canView.adminPanel = action.payload.user.canView.adminPanel;
            state.user.canView.documentation = action.payload.user.canView.documentation;
            state.user.canView.documentationEditor = action.payload.user.canView.documentationEditor;
            state.user.canView.support = action.payload.user.canView.support;
            state.user.canView.signOut = action.payload.user.canView.signOut;
            state.user.canView.editWidgets = action.payload.user.canView.editWidgets;
            state.user.canView.setAssistantSimple = action.payload.user.canView.setAssistantSimple;
            state.user.canView.accessGroups = action.payload.user.canView.accessGroups;
            state.user.canView.mailDeputy = action.payload.user.canView.mailDeputy;
            state.user.canView.auditor = action.payload.user.canView.auditor;
            state.user.canView.readonlyAuditor = action.payload.user.canView.readonlyAuditor;
            state.user.canView.dashboards = action.payload.user.canView.dashboards;
        },
        successLogout: (state) => {
            state.isAuth = false;
            state.user = {} as IUser;
            state.user.canView = {} as ICanView;
        },
        /** Сменился access сессии (вход, ротация, соседняя вкладка, выход) — без затрагивания user-полей. */
        accessTokenChanged: (state, action: PayloadAction<boolean>) => {
            state.hasAccessToken = action.payload;
        },
        /**
         * Эквивалент successLogout по эффекту на state, но отдельный action —
         * чтобы authListenerMiddleware мог повесить на него тост «сессия истекла»
         * и, при необходимости, аналитику. Диспатчит startAuth, когда сессия кончилась
         * не по «Выйти» (отказ ротации, 401 без refresh, выход в соседней вкладке); `user-switch` — в соседней
         * вкладке вошёл другой пользователь (свой текст тоста).
         */
        sessionExpired: {
            reducer: (state) => {
                state.isAuth = false;
                state.user = {} as IUser;
                state.user.canView = {} as ICanView;
            },
            prepare: (cause?: SessionExpiredCause) => ({ payload: cause }),
        },
        setSignInSettings: (state, action: PayloadAction<ISignInSettings | null>) => {
            state.signInSettings = action.payload;
        },
        setApiUnavailable: (state, action: PayloadAction<boolean>) => {
            state.apiUnavailable = action.payload;
        },
    },
});

export const {
    setIsLoading,
    setIsLoggingIn,
    successLogin,
    successLogout,
    setError,
    setSignInSettings,
    setApiUnavailable,
} = AuthSlice.actions;

export default AuthSlice.reducer;
